import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

export default function DoctorAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = () => {
    setLoading(true);
    api.get('/appointments').then(r => setAppointments(r.data.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const confirm = async (id) => {
    try {
      await api.post('/appointments/' + id + '/confirm');
      toast.success('RDV confirmé'); load();
    } catch(e) { toast.error('Erreur'); }
  };

  const complete = async (id) => {
    try {
      await api.post('/appointments/' + id + '/complete');
      toast.success('RDV terminé'); load();
    } catch(e) { toast.error('Erreur'); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Annuler ce rendez-vous ?')) return;
    try {
      await api.post('/appointments/' + id + '/cancel');
      toast.success('RDV annulé'); load();
    } catch(e) { toast.error('Erreur'); }
  };

  const confirmCash = async (id) => {
    try {
      await api.post('/payments/cash/' + id);
      toast.success('Paiement espèces confirmé'); load();
    } catch(e) { toast.error('Erreur paiement'); }
  };

  const statusColor = (s) => ({
    pending:'bg-yellow-100 text-yellow-700',
    confirmed:'bg-green-100 text-green-700',
    cancelled:'bg-red-100 text-red-700',
    completed:'bg-blue-100 text-blue-700',
  }[s] || 'bg-gray-100 text-gray-700');

  const statusLabel = (s) => ({
    pending:'En attente', confirmed:'Confirmé',
    cancelled:'Annulé', completed:'Terminé'
  }[s] || s);

  const filtered = filter === 'all' ? appointments
    : appointments.filter(a => a.status === filter);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/doctor/dashboard" className="text-green-600 font-medium">← Retour</Link>
        <h1 className="font-bold text-gray-800">Mes rendez-vous</h1>
      </div>
      <div className="max-w-lg mx-auto p-4 space-y-4">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[['all','Tous'],['pending','En attente'],['confirmed','Confirmés'],['completed','Terminés'],['cancelled','Annulés']].map(([val,label]) => (
            <button key={val} onClick={() => setFilter(val)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter===val ? 'bg-green-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? <p className="text-center text-gray-400 py-8">Chargement...</p>
        : filtered.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-4xl mb-3">📅</p>
            <p className="text-gray-500">Aucun rendez-vous</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(apt => (
              <div key={apt.id} className="card">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-bold text-gray-800">{apt.patient?.name}</p>
                    <p className="text-sm text-gray-500">📱 {apt.patient?.phone}</p>
                    <p className="text-sm text-gray-500">
                      📅 {new Date(apt.scheduled_at).toLocaleDateString('fr-FR', {weekday:'short', day:'numeric', month:'short'})}
                      {' '}🕐 {new Date(apt.scheduled_at).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {apt.type === 'in_person' ? '🏥 Cabinet' : apt.type === 'teleconsultation' ? '📹 Vidéo' : '🏠 Domicile'}
                      {' • '}{parseInt(apt.amount).toLocaleString()} FCFA
                      {' • '}{apt.payment_status === 'paid' ? '✅ Payé' : '⏳ Non payé'}
                    </p>
                    {apt.reason && <p className="text-xs text-gray-500 mt-1 italic">"{apt.reason}"</p>}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${statusColor(apt.status)}`}>
                    {statusLabel(apt.status)}
                  </span>
                </div>

                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap">
                  {apt.status === 'pending' && (
                    <button onClick={() => confirm(apt.id)}
                      className="flex-1 bg-green-600 text-white py-2 rounded-xl text-xs font-medium">
                      ✓ Confirmer
                    </button>
                  )}
                  {apt.status === 'confirmed' && (
                    <button onClick={() => complete(apt.id)}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-xl text-xs font-medium">
                      ✓ Terminé
                    </button>
                  )}
                  {apt.payment_status === 'pending' && apt.payment_method === 'cash' && apt.status !== 'cancelled' && (
                    <button onClick={() => confirmCash(apt.id)}
                      className="flex-1 bg-yellow-500 text-white py-2 rounded-xl text-xs font-medium">
                      💵 Paiement reçu
                    </button>
                  )}
                  {['pending','confirmed'].includes(apt.status) && (
                    <button onClick={() => cancel(apt.id)}
                      className="flex-1 border border-red-200 text-red-500 py-2 rounded-xl text-xs font-medium">
                      Annuler
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
